import {
	ActionIcon,
	Box,
	createStyles,
	getStylesRef,
	Group,
	Menu,
	TextInput,
	UnstyledButton,
} from '@mantine/core';
import type { EmbeddedPromptParentOut } from '@repo/api-codegen';
import { Icon, IconButton, Text } from '@repo/foundations';
import { useCallback } from 'react';
import {
	sanitizePromptMessage,
	toSentenceCase,
} from '../../../components/AIAssistant/utils';
import { useFeatureFlags } from '../../../utils/featureFlags';

interface IPromptButtonProps {
	chat: EmbeddedPromptParentOut;
	isActive: boolean;
	isPinned: boolean;
	onSelectChat: (chat: EmbeddedPromptParentOut) => void;
	onDeleteChat: (chat: EmbeddedPromptParentOut) => void;
	editingChatId: string | null;
	editingChatTitle: string;
	onStartEditingChatTitle: (chatId: string, currentTitle: string) => void;
	onStopEditingChatTitle: () => void;
	onSaveEditingChatTitle: (chatId: string) => void;
	onModifyEditingChatTitle: (title: string) => void;
	onTogglePin: (chatId: string) => void;
}

const useStyles = createStyles((theme) => ({
	button: {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: `${theme.spacing['4xs']} ${theme.spacing['4xs']} ${theme.spacing['4xs']} ${theme.spacing.xs}`,
		gap: theme.spacing.xs,
		borderRadius: theme.radius.sm,
		backgroundColor: theme.other.getColor('fill/transparent/default'),

		'&:hover': {
			backgroundColor: theme.other.getColor('fill/transparent/hover'),
			[`& .${getStylesRef('menuButton')}`]: {
				opacity: 1,
				width: 'auto',
			},
		},
	},
	buttonActive: {
		backgroundColor: theme.other.getColor('fill/transparent/selected'),
	},
	menuButton: {
		ref: getStylesRef('menuButton'),
		opacity: 0,
		width: 0,
	},
	title: {
		width: '100%',
		minWidth: 0,
	},
	editTitle: {
		padding: 0,
		fontSize: theme.fontSizes.sm,
		fontWeight: theme.other.typography.weight.semibold,
		height: 'auto',
		lineHeight: 'inherit',
		'&:focus': {
			boxShadow: 'none',
		},
	},
	indicator: {
		display: 'inline-block',
		flexShrink: 0,
		width: 8,
		height: 8,
		borderRadius: 8,
		backgroundColor: theme.other.getColor('icon/emphasis/default'),
	},
}));

export function ChatHistoryItem({
	chat,
	isActive,
	isPinned,
	onSelectChat,
	onDeleteChat,
	editingChatId,
	editingChatTitle,
	onStartEditingChatTitle,
	onStopEditingChatTitle,
	onSaveEditingChatTitle,
	onModifyEditingChatTitle,
	onTogglePin,
}: IPromptButtonProps) {
	const { classes, cx } = useStyles();
	const isEditing = editingChatId === chat.id;
	const { secodaAiTasks } = useFeatureFlags();

	const handleSelectChat = useCallback(() => {
		// Prevent opening the chat when clicking the input box while editing
		if (!isEditing) {
			onSelectChat(chat);
		}
	}, [onSelectChat, chat, isEditing]);

	const handleTitleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter') {
				onSaveEditingChatTitle(chat.id);
			} else if (e.key === 'Escape') {
				onStopEditingChatTitle();
			}
		},
		[onSaveEditingChatTitle, onStopEditingChatTitle, chat.id]
	);

	const handleInputRef = useCallback(
		(node: HTMLInputElement | null) => {
			if (node && isEditing) {
				node.focus();
				node.select();
			}
		},
		[isEditing]
	);

	return (
		<UnstyledButton
			className={cx(classes.button, {
				[classes.buttonActive]: isActive,
			})}
			onClick={handleSelectChat}
			data-testid={`chat-history-item-${chat.id}`}
		>
			{isEditing ? (
				<TextInput
					ref={handleInputRef}
					value={editingChatTitle}
					onChange={(e) => onModifyEditingChatTitle(e.target.value)}
					onKeyDown={handleTitleKeyDown}
					placeholder="Enter chat title..."
					variant="unstyled"
					classNames={{
						root: classes.title,
						input: classes.editTitle,
					}}
				/>
			) : (
				<Group spacing="2xs" noWrap style={{ flex: 1, minWidth: 0 }}>
					{secodaAiTasks && chat?.has_unread_agent_message ? (
						<Box className={classes.indicator} style={{ flexShrink: 0 }} />
					) : null}
					<Text
						size="sm"
						weight="semibold"
						truncate
						style={{ flex: 1, minWidth: 0 }}
					>
						{toSentenceCase(sanitizePromptMessage(chat.title ?? chat.prompt))}
					</Text>
				</Group>
			)}
			{!isEditing ? (
				<Menu shadow="md" offset={2}>
					<Menu.Target>
						<ActionIcon
							size="xs"
							onClick={(e) => e.stopPropagation()}
							className={classes.menuButton}
						>
							<Icon name="dots" size="md" />
						</ActionIcon>
					</Menu.Target>
					<Menu.Dropdown>
						<Menu.Item
							onClick={(e) => {
								e.stopPropagation();
								onTogglePin(chat.id);
							}}
							icon={
								<Icon
									name="pinned"
									fillColor={isPinned ? 'icon/primary/default' : undefined}
								/>
							}
						>
							{isPinned ? 'Unpin' : 'Pin'}
						</Menu.Item>
						<Menu.Item
							icon={<Icon name="pencil" />}
							onClick={(e) => {
								e.stopPropagation();
								const currentTitle = toSentenceCase(
									sanitizePromptMessage(chat.title ?? chat.prompt ?? '')
								);
								onStartEditingChatTitle(chat.id, currentTitle);
							}}
						>
							Rename...
						</Menu.Item>
						<Menu.Divider />
						<Menu.Item
							icon={<Icon name="trash" />}
							onClick={(e) => {
								e.stopPropagation();
								onDeleteChat(chat);
							}}
						>
							Delete...
						</Menu.Item>
					</Menu.Dropdown>
				</Menu>
			) : (
				<Group spacing={0} noWrap>
					<IconButton
						iconName="x"
						variant="tertiary"
						tooltip="Cancel"
						onClick={onStopEditingChatTitle}
					/>
					<IconButton
						iconName="check"
						variant="tertiary"
						tooltip="Save"
						onClick={() => onSaveEditingChatTitle(chat.id)}
					/>
				</Group>
			)}
		</UnstyledButton>
	);
}
